<template>
  <!-- Table Container Card -->
  <b-card no-body>
    <div
      v-if="loanScheduleEditSuccessCount.length || loanScheduleEditFailed.length"
      class="m-2"
    >
      <b-row>
        <b-col
          v-if="loanScheduleEditSuccessCount.length"
          md="6"
          class="mb-2"
        >
          <b-alert
            show
            variant="primary"
            class="p-2"
          >
            {{ loanScheduleEditSuccessCount }}
          </b-alert>
        </b-col>
        <b-col
          v-if="loanScheduleEditFailed.length"
          md="6"
          class="mb-2"
        >
          <div>
            <b-button variant="danger">
              {{ $t('FAILED LISTS') }}
            </b-button>
          </div>
          <b-table
            ref="loanScheduleEditFailed"
            :items="loanScheduleEditFailed"
            responsive
            :fields="failedHeaders"
            primary-key="id"
            show-empty
            :empty-text="$t('No records available')"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            class="position-relative"
          /></b-col>
      </b-row>
    </div>

    <div class="m-2">
      <b-row class="d-flex align-items-center">
        <b-col md="5">
          <validation-observer ref="batchLoanScheduleEditRules">
            <b-form>
              <b-col
                md="12"
                class="mb-2"
              >
                <div class="mb-1">
                  {{ $t('Select file (.xlsx format only)') }}
                </div>
                <b-form-file
                  id="file-name"
                  v-model="hasFileData"
                  :label="$t('Select File (.xlsx format only)')"
                  :state="Boolean(hasFileData)"
                  :placeholder="$t('Choose a file or drop it here...')"
                  :drop-placeholder="$t('Drop file here...')"
                  error-name="file"
                  rules="required"
                  accept=".xlsx"
                  @change="onChangeXLSX($event)"
                />
              </b-col>
            </b-form>
          </validation-observer>
        </b-col>

        <b-col
          md="7"
        >
          <b-row>
            <b-col
              md="4"
              class="mt-2"
            >
              <SwitchButton
                v-model="enablePreview"
                :label="$t('Enable Preview?')"
                name="enable-preview"
              />
            </b-col>
            <b-col md="3">
              <Button
                v-if="getUserPermission('MODIFY_PAYMENT_SCHEDULE')"
                :label="$t('Create Loan Schedule')"
                size="md"
                :loading="isLoading"
                :disabled="isDisabled"
                @click="handleSubmit"
              />
            </b-col>
            <b-col md="5">
              <Button
                class="ml-lg-2"
                align="end"
                :label="$t('Download Batch Loan Schedule Format')"
                size="md"
                icon="DownloadIcon"
                show-icon
                :loading="isLoading2"
                :disabled="isDisabled2"
                @click="handleDownloadSubmit"
              />
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </div>
    <b-table
      v-if="errorMessages.length"
      ref="loanScheduleEditSuccessCountReport"
      :items="errorMessages"
      responsive
      :fields="translatedErrorHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    />
    <p
      v-if="totalSheetRows && !enablePreview"
      class="p-2"
      style="font-size: 18px;"
    >
      {{ $t('There are total of') }} ({{ totalSheetRows }}) {{ $t('Records fetched') }}
    </p>
    <b-table
      v-if="enablePreview"
      ref="batchLoanScheduleRef"
      :items="parseExcelFile"
      responsive
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(principal)="data">
        {{ toAmount(data.value) }}
      </template>
      <template #cell(interest)="data">
        {{ toAmount(data.value) }}
      </template>
      <template #cell(fee)="data">
        {{ toAmount(data.value) }}
      </template>
    </b-table>

    <TablePagination
      v-model="currentPage"
      name="table-pagination"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BForm, BTable, BFormFile, BButton, BAlert,
} from 'bootstrap-vue'
import { required } from '@validations'
import { ValidationObserver } from 'vee-validate'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions, mapGetters } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import XLSX from 'xlsx'
import { toAmount, toKobo } from '@/helpers/formatAmount'
import Button from '../../components/button/Button.vue'
import SwitchButton from '../../components/switch/Switch.vue'
import { batchAccountFormat, tableAllHeaders } from '../../../helpers/data'
import TablePagination from '@/views/components/table/TablePagination.vue'
import { toYearWithDate } from '@/helpers/formatDate'

export default {
  components: {
    BCard,
    BTable,
    BRow,
    BCol,
    BForm,
    BAlert,
    BFormFile,
    ValidationObserver,
    Button,
    BButton,
    SwitchButton,
    TablePagination,
  },
  filters: {
    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toAmount,
      toKobo,
      enablePreview: false,
      totalSheetRows: '',
      fileData: [],
      hasFileData: [],
      parseExcelFile: [],
      validatedExcelFileData: [],
      sortOrders: {},
      accountFormat: batchAccountFormat,
      successHeaders: tableAllHeaders,
      // failedHeaders: tableLessHeaders,

      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'loanAccountNumber', sortable: true, label: 'Loan Account Number' },
        { key: 'principal', sortable: true, label: 'Principal' },
        { key: 'interest', sortable: true, label: 'Interest' },
        { key: 'fee', sortable: true, label: 'Fee' },
        { key: 'paymentDate', sortable: true, label: 'Payment Date' },
      ],
      required,
      isDisabled: false,
      isLoading: false,
      isDisabled2: false,
      isLoading2: false,
      showFormError: false,
      isCreateBatchHidden: true,
      loanScheduleEditSuccessCount: [],
      loanScheduleEditFailed: [],
      sortDesc: false,
      sortBy: 'loanAccountNumber',
      errorHeaders: [
        { key: 'referenceNumber', sortable: true, label: 'Reference Number' },
        { key: 'message', sortable: true, label: 'Error Messages' },
      ],
      errorMessages: [],
    }
  },
  computed: {
    ...mapGetters({
      userDetails: 'auth/getCurrentUser',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedErrorHeaders() {
      return this.errorHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    failedHeaders() {
      return this.tableLessHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  methods: {
    ...mapActions({
      batchLoanScheduleEdit: 'batch/batchLoanScheduleEditAction',
      downloadAccountFormat: 'batch/downloadAccountFormat',
    }),
    async handleSubmit() {
      this.$refs.batchLoanScheduleEditRules.validate().then(success => {
        if (success) {
          this.confirm(this.batchLoanScheduleAction, { text: this.$t('Are you sure you want to Create Batch Loan Schedule?') })
        }
      })
    },
    async batchLoanScheduleAction() {
      try {
        this.loanScheduleEditSuccessCount = ''
        this.showFormError = false
        this.isDisabled = true
        this.isLoading = true
        const formData = this.parseExcelFile.map(item => ({
          ...item,
          principal: item.principal ? toKobo(item.principal) : 0,
          interest: item.interest ? toKobo(item.interest) : 0,
          fee: item.fee ? toKobo(item.fee) : 0,
          paymentDate: item.paymentDate ? toYearWithDate(item.paymentDate) : null,
        }))
        const loanSchedules = [...formData]
        const initiatedBy = this.userDetails.email
        const approvedBy = ''
        const response = await this.batchLoanScheduleEdit({ loanSchedules, initiatedBy, approvedBy })
        const { status, data } = response
        if (status === 200) {
          this.hasFileData = []
          this.parseExcelFile = []
          if (data.successCount > 0) {
            const createdText = this.$t('Batch Loan Schedule was successfully created')
            this.loanScheduleEditSuccessCount = `(${data.successCount}) ${createdText}`
          }
          this.loanScheduleEditFailed = data.failed

          this.isDisabled = false
          this.isLoading = false
          this.totalSheetRows = ''
        }
      } catch (error) {
        const reasonText = this.$t('Your request to create Batch Loan Schedule failed. Reason:')
        error.response.data.errors.forEach(responseError => {
          this.alertError({ text: `${reasonText} ${responseError.errorMessage}` })
        })
        this.isDisabled = false
        this.isLoading = false
        this.showFormError = true
      } finally {
        this.isDisabled = false
        this.isLoading = false
      }
    },
    onChangeXLSX(event) {
      this.errorMessages = []
      this.isCreateBatchHidden = true
      this.fileData = event.target.files ? event.target.files[0] : null
      if (this.fileData) {
        const vm = this
        const reader = new FileReader()
        reader.onload = e => {
          const data = e.target.result
          const workbook = XLSX.read(data, { type: 'binary', cellDates: true, dateNF: 'mm/dd/yyyy;@' })
          const sheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[sheetName]
          const rowObject = XLSX.utils.sheet_to_row_object_array(worksheet, { raw: false })
          vm.totalSheetRows = rowObject.length
          vm.parseExcelFile = JSON.parse(JSON.stringify(rowObject)).map(items => ({
            loanAccountNumber: items['Loan Account Number'],
            principal: items.Principal,
            interest: items.Interest,
            fee: items.Fee,
            paymentDate: items['Payment Date'],
          }))
        }
        reader.readAsArrayBuffer(this.fileData)
      }
    },
    async handleDownloadSubmit() {
      this.confirm(this.downloadAction, { text: this.$t('Are you sure you want to download Batch Loan Schedule?') })
    },
    async downloadAction() {
      try {
        this.isLoading2 = true
        this.isDisabled2 = true
        const LOAN_REPAYMENT_SCHEDULE_EDIT = 25
        const response = await this.downloadAccountFormat(LOAN_REPAYMENT_SCHEDULE_EDIT)
        if (response.status === 200) {
          const downloadAnchor = document.createElement('a')
          downloadAnchor.setAttribute('href', response.data.data)
          const filename = 'Udara360-Batch-Loan-Schedule.xlsx'
          downloadAnchor.setAttribute('download', filename)
          document.body.appendChild(downloadAnchor)
          downloadAnchor.click()
          document.body.removeChild(downloadAnchor)
          this.alertSuccess({ text: this.$t('Your request to download Batch Loan Schedule Format was successful') })
        }
      } catch (error) {
        const reasonText = this.$t('Your request to download Batch Loan Schedule Format failed. Reason:')
        error.response.data.errors.forEach(responseError => {
          this.alertError({ text: `${reasonText} ${responseError.errorMessage}` })
          this.isLoading2 = false
          this.isDisabled2 = false
        })
      } finally {
        this.isLoading2 = false
        this.isDisabled2 = false
      }
    },
  },
}
</script>
